import type { TFunction } from 'i18next';
import { when } from 'mobx';

import type {
  CustomFields,
  IPredicate,
  IStoredPredicate,
  TComparison,
  TPredicateKind,
} from '@feathr/blackbox';
import { FieldCollection, FieldDataType, Segment } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { moment } from '@feathr/hooks';
import type { Model } from '@feathr/rachis';

import defaultBreadcrumbFields from '@feathr/blackbox/fixtures/defaultBreadcrumbFields.json';
import defaultPersonFields from '@feathr/blackbox/fixtures/defaultPersonFields.json';

const { bool, date, float, int, list, str } = FieldDataType;

const UNSUBSCRIBE_MESSAGE_VALUE = '"global": true';

/** Comparisons that take no value; the value input is hidden for them. */
const EXISTENCE_COMPARISONS: TComparison[] = ['exists', 'nexists'];

export function isExistenceComparison(comparison?: TComparison): boolean {
  return !!comparison && EXISTENCE_COMPARISONS.includes(comparison);
}

export type TOptionsGroup =
  | 'campaign'
  | 'flight'
  | 'segment'
  | 'form'
  | 'campaignGoal'
  | 'flightGoal'
  | 'facebookCampaign'
  | 'emailCampaignCrumbTrigger'
  | 'emailCampaignDateTrigger';

const EMAIL_TRIGGER_GROUPS: TOptionsGroup[] = [
  'emailCampaignCrumbTrigger',
  'emailCampaignDateTrigger',
];

export function isEmailTrigger(optionsGroup?: TOptionsGroup): boolean {
  return !!optionsGroup && EMAIL_TRIGGER_GROUPS.includes(optionsGroup);
}

interface IGetActivitiesArgs {
  CustomFields: CustomFields;
  model: Model;
  isAnyFeathrPlatform: boolean;
  optionsGroup: TOptionsGroup;
  t: TFunction;
}

export interface IAttrOption extends ISelectOption {
  helpText: string;
  kind: FieldCollection.Breadcrumb | FieldCollection.Person;
  type: FieldDataType;
}

export interface IComparisonOption {
  id: TComparison;
  name: string;
}

export function getComparisonOptions(
  { id, type }: Pick<IAttrOption, 'id' | 'type'>,
  t: TFunction,
): IComparisonOption[] {
  const {
    array,
    equality,
    existence,
    numerical,
    string,
    temporal,
  }: Record<string, IComparisonOption[]> = {
    equality: [
      { id: 'eq', name: t('is') },
      { id: 'ne', name: t('is not') },
    ],
    existence: [
      { id: 'exists', name: t('exists') },
      { id: 'nexists', name: t('does not exist') },
    ],
    array: [
      { id: 'in', name: t('contains') },
      { id: 'nin', name: t('does not contain') },
    ],
    numerical: [
      { id: 'eq', name: t('equals') },
      { id: 'ne', name: t('does not equal') },
      { id: 'gt', name: t('is greater than') },
      { id: 'lt', name: t('is less than') },
    ],
    string: [
      { id: 'starts_with', name: t('starts with') },
      { id: 'wildcard', name: t('matches pattern') },
      { id: 'regexp', name: t('matches the expression') },
      { id: 'contains_substring', name: t('contains') },
    ],
    temporal: [
      { id: 'gt', name: t('is after') },
      { id: 'lt', name: t('is before') },
      { id: 'gte', name: t('is after or on') },
      { id: 'lte', name: t('is before or on') },
    ],
  };

  switch (type) {
    case str: {
      const baseOptions = [...existence, ...equality];
      // Don't allow string comparisons for ObjectId fields.
      if (id.includes('_id')) {
        return [...baseOptions, ...array];
      }
      return [...baseOptions, ...string];
    }

    case int:

    case float:
      return [...existence, ...numerical];

    case bool:
      return equality;

    case date:
      return [...existence, ...equality, ...temporal];

    case list:
      return [...existence, ...array];

    default:
      return existence;
  }
}

export async function getAttributes(
  t: TFunction,
  CustomFields: CustomFields,
): Promise<IAttrOption[]> {
  const helpText = t('This is a custom field that has been added to your Feathr database.');

  const customAttributes = await CustomFields.list({
    filters: {
      collection: FieldCollection.Person,
    },
    pagination: {
      page: 0,
      page_size: 1000,
    },
  });

  await when(() => !customAttributes.isPending);
  const options = customAttributes.models.map((field) => ({
    helpText: field.get('description', helpText),
    id: `custom.${field.get('f_key')}`,
    kind: field.get('collection'),
    name: field.get('u_key'),
    type: field.get('data_type'),
  }));

  return [...defaultPersonFields, ...options];
}

/*
 * Check if any entry in model.predicates[0].group has the required conditions.
 * Attribute being 'msg' and value being '"global": true' means that the user has unsubscribed from all messages from a campaign.
 */
export function hasGlobalMsgCondition(model: Model): boolean {
  if (!(model instanceof Segment)) {
    return false;
  }
  const predicates = model.get('predicates');
  let firstPredicate: IPredicate | undefined;

  if (predicates && predicates.length > 0) {
    firstPredicate = predicates[0];
  } else {
    firstPredicate = undefined;
  }

  if (firstPredicate && firstPredicate.group) {
    return firstPredicate.group.some(
      (entry: IPredicate) =>
        entry.value === UNSUBSCRIBE_MESSAGE_VALUE && entry.attr_against === 'msg',
    );
  }

  return false;
}

export async function getActivities({
  CustomFields,
  model,
  isAnyFeathrPlatform,
  optionsGroup,
  t,
}: IGetActivitiesArgs): Promise<IAttrOption[]> {
  const helpText = t('This is a custom field that has been added to your Feathr database.');

  const customActivities = CustomFields.list({
    filters: {
      collection: FieldCollection.Breadcrumb,
    },
    pagination: {
      page: 0,
      page_size: 1000,
    },
  });

  await when(() => !customActivities.isPending);
  const options = customActivities.models.map((field) => ({
    helpText: field.get('description', helpText),
    id: `cust_params.${field.get('f_key')}`,
    kind: field.get('collection'),
    name: field.get('u_key'),
    type: field.get('data_type'),
  }));

  function shouldRender(field: ISelectOption): boolean {
    const render: boolean = model instanceof Segment ? model.get('read_only') : false;
    const globalMsgCondition: boolean = hasGlobalMsgCondition(model);
    const gated = [
      {
        id: 'p_id',
        render: isAnyFeathrPlatform,
      },
      {
        id: 'q_id',
        render,
      },
      {
        id: 'msg',
        render: render || globalMsgCondition,
      },
    ];

    const gatedAttribute = gated.find(({ id }) => id === field.id);

    // If it's a gated attribute, we want to check its render conditions.
    if (gatedAttribute) {
      return gatedAttribute.render;
    } else if (isEmailTrigger(optionsGroup)) {
      /**
       * There are certain activity predicates we don't want used
       * in email campaign groups.
       */
      return !['brow', 'pform', 'd_c', 'isp'].includes(field.id);
    } else {
      return true;
    }
  }

  return [...defaultBreadcrumbFields.filter(shouldRender), ...options];
}

export function toPredicateAttrType(
  type: FieldDataType,
  comparison?: TComparison,
): 'string' | 'boolean' | 'list' | 'date' | 'integer' | 'float' {
  // Override attribute type for array comparisons.
  if (comparison && ['in', 'nin'].includes(comparison)) {
    return 'list';
  }

  switch (type) {
    case str:
      return 'string';

    case int:
      return 'integer';

    case bool:
      return 'boolean';

    default:
      return type;
  }
}

export function fieldToKind(
  fieldCollection: FieldCollection.Breadcrumb | FieldCollection.Person,
  optionsGroup?: TOptionsGroup,
): TPredicateKind {
  const { Breadcrumb, Person } = FieldCollection;
  const map = {
    [Breadcrumb]: 'activity',
    [Person]: optionsGroup === 'emailCampaignCrumbTrigger' ? 'update' : 'attribute',
  };

  return map[fieldCollection] as TPredicateKind;
}

export interface IRelativeDateOptions {
  daysAgo: ISelectOption;
  daysFromNow: ISelectOption;
}

export function getRelativeDateOptionsMap(
  t: TFunction,
  value: string | number,
): IRelativeDateOptions {
  // Only map the options if the count is a number (predicate value can be a string).
  const count = typeof value === 'number' ? value : 1;

  const relativeDateOptions = [
    { id: '-', name: t('day ago', { count }) },
    { id: '+', name: t('day from now', { count }) },
  ];

  return {
    daysAgo: relativeDateOptions[0],
    daysFromNow: relativeDateOptions[1],
  };
}

export interface IDateComparisonOptions {
  today: ISelectOption;
  exactDate: ISelectOption;
  exactDateAndTime: ISelectOption;
  relativeDate: ISelectOption;
}

export function getDateComparisonOptionsMap(t: TFunction): IDateComparisonOptions {
  const dateComparisonOptions = [
    {
      name: t('today'),
      /*
       * Maps to a `_date_r` comparison with a 0 value and does not
       * reference its own unique comparison operator.
       */
      id: 'today',
    },
    {
      name: t('exact date'),
      // Maps to new comparison operators: eq_date, ne_date, gt_date and lt_date.
      id: '_date',
    },
    {
      name: t('exact date and time'),
      // Maps to the existing comparison operators, eq, ne, gt and lt.
      id: 'date_time',
    },
    {
      name: t('relative date'),
      // Maps to new comparison operators: eq_date_r, ne_date_r, gt_date_r and lt_date_r.
      id: '_date_r',
    },
  ];

  return {
    today: dateComparisonOptions[0],
    exactDate: dateComparisonOptions[1],
    exactDateAndTime: dateComparisonOptions[2],
    relativeDate: dateComparisonOptions[3],
  };
}

export function getInitialValueForDate(
  comparison: string,
  currentValue?: string | number | boolean | string[],
): string | number {
  if (isExistenceComparison(comparison as TComparison)) {
    return '';
  }

  // Filter out incompatible types for date handling
  const validCurrentValue =
    typeof currentValue === 'string' || typeof currentValue === 'number' ? currentValue : undefined;

  const now = moment.utc();
  if (comparison.endsWith('_date')) {
    // Returns ISO format with current date only
    return now.startOf('day').format();
  }

  if (!comparison.includes('_')) {
    // Returns ISO format with current time minus seconds and milliseconds
    return now.seconds(0).milliseconds(0).format();
  }

  // Handle "today" case - if comparison ends with _date_r and current value is 0, preserve it
  if (comparison.endsWith('_date_r') && validCurrentValue === 0) {
    return 0;
  }

  return 1;
}

export interface IComparisonChange {
  comparison: TComparison;
  /** A value encoded by the selected option itself (e.g. "today" = 0, relative date default = 1). */
  value?: IPredicate['value'];
}

/**
 * Derives the full predicate update for a comparison change: the comparison
 * itself plus whatever value/attr_type coercion the transition requires
 * (existence comparisons clear the value, in/nin convert between string and
 * list, date comparisons get an initial value unless the option encoded one).
 */
export function deriveComparisonChange(
  current: IPredicate,
  { comparison, value }: IComparisonChange,
): Partial<IStoredPredicate> {
  // Handle string-based values when the comparison changes.
  if (['string', 'list'].includes(current.attr_type ?? '')) {
    if (isExistenceComparison(comparison)) {
      return { comparison, value: '' };
    }
    // Array comparisons (in/nin) need attr_type 'list' and value as array.
    if (['in', 'nin'].includes(comparison)) {
      const currentValue = current.value;
      let arrayValue: string[] = [];
      if (Array.isArray(currentValue)) {
        arrayValue = currentValue;
      } else if (currentValue) {
        arrayValue = [String(currentValue)];
      }
      return { comparison, attr_type: 'list', value: arrayValue };
    }
    // Switching back from array comparison to non-array - ensure attr_type is 'string'.
    if (current.attr_type === 'list') {
      const currentValue = current.value;
      const stringValue = Array.isArray(currentValue) ? currentValue[0] ?? '' : currentValue;
      return { comparison, attr_type: 'string', value: stringValue };
    }
    return { comparison };
  }

  if (current.attr_type === 'date') {
    // An option-encoded value (e.g. "today" = 0) wins over the derived default.
    return {
      comparison,
      value: value !== undefined ? value : getInitialValueForDate(comparison, current.value),
    };
  }

  return { comparison };
}

/** Derives the full predicate update for selecting a rule (attribute/activity). */
export function deriveRuleChange(
  option: IAttrOption,
  { optionsGroup }: { optionsGroup?: TOptionsGroup },
  t: TFunction,
): Partial<IStoredPredicate> {
  // Date-triggered sends compare a date attribute relatively; "1 day from now" is the default.
  if (optionsGroup === 'emailCampaignDateTrigger') {
    return {
      attr_against: option.id,
      attr_type: toPredicateAttrType(option.type, 'eq_date_r'),
      kind: fieldToKind(option.kind, optionsGroup),
      comparison: 'eq_date_r',
      value: 1,
    };
  }
  /*
   * Derive attr_type from the NEW comparison, not the old one. Reusing the
   * previous comparison (e.g. 'in' from a Tag filter) would force attr_type to
   * 'list' regardless of the new field's type, leaving the predicate
   * inconsistent and hiding the comparison/value inputs.
   */
  const newComparison = getComparisonOptions({ id: option.id, type: option.type }, t)[0].id;
  const newAttrType = toPredicateAttrType(option.type, newComparison);
  return {
    attr_against: option.id,
    attr_type: newAttrType,
    kind: fieldToKind(option.kind, optionsGroup),
    comparison: newComparison,
    value: newAttrType === 'boolean' ? false : '',
  };
}

/**
 * Finds the field option a stored predicate refers to: the inverse of
 * deriveRuleChange's write, matching on the same three fields it sets.
 */
export function findRuleOption(
  predicate: IPredicate,
  options: IAttrOption[],
  optionsGroup?: TOptionsGroup,
): IAttrOption | undefined {
  return options.find(
    ({ id, kind, type }) =>
      predicate.attr_against === id &&
      predicate.attr_type === toPredicateAttrType(type, predicate.comparison) &&
      predicate.kind === fieldToKind(kind, optionsGroup),
  );
}

/*
 * Collapse a rule group to the update shape for its containing predicate: a
 * single remaining rule inlines onto the predicate itself, dropping the rule's
 * `key`, `group`, `excludes`, and `group_mode` so the container keeps its own
 * (the row's key is how edits resolve the row; excludes and group_mode are
 * container-level concerns, not per-rule ones). Multiple rules nest under
 * `group` and the container keeps only `kind`.
 */
export function groupToPredicateUpdate(rules: IStoredPredicate[]): Partial<IStoredPredicate> {
  // Unreachable from the editor (the last rule of a group can't be removed);
  // guards exported use so an empty list clears the group instead of throwing.
  if (rules.length === 0) {
    return { group: [] };
  }
  if (rules.length === 1) {
    const { excludes, group, group_mode, key, ...rule } = rules[0];
    return { ...rule, group: [] };
  }
  return {
    group: rules,
    attr_against: undefined,
    attr_type: undefined,
    kind: rules[0].kind,
    comparison: undefined,
    value: undefined,
  };
}
